/**
 * Calendar maths for the `date` field's picker. **Pure** — no DOM, no SDK, no config.
 *
 * 🚨 Reachable from `@cod/form-tree/pure`, and deliberately so. Both bundles render a date picker
 * (`MIGRATION.md` §12.0), and the two must agree on *every* answer this file gives: which day the
 * 1st falls on, how many rows the grid has, what a picked cell stores. A month that starts on a
 * different weekday on Amazonas than on Patagonia is the same store looking broken on half its
 * traffic — the identical argument that keeps `offers.logic.ts` shared.
 *
 * ## Why there is a picker at all
 *
 * `FormFieldInputType` has no `date` and a Web Worker has no native picker, so the migration
 * converged both paths onto a masked `dd/mm/aaaa` text input (`MIGRATION.md` §2.1). That removed the
 * native calendar without replacing it — the buyer had to know the format and type it. This is the
 * replacement, and it is **built from primitives on both paths** rather than restoring
 * `<input type="date">` on the legacy one: the native control is still gone, and the convergence
 * that decision bought is intact.
 *
 * ## The stored value never changed
 *
 * `state.values[key]` is still `dd/mm/yyyy`, still validated by `isValidDate`, still collected by
 * `collectFields` and posted in the same shape. The picker is a new way to *produce* that string,
 * not a new format — the backend and every stored order are untouched.
 */
/** The displayed format, and the placeholder a merchant who sets none inherits. */
export const DATE_FORMAT = 'dd/mm/yyyy';
export const DATE_PLACEHOLDER = 'dd/mm/aaaa';
const dateRe = /^(\d{2})\/(\d{2})\/(\d{4})$/;
/** Days in a month. Day 0 of the NEXT month is the last of this one, so leap years fall out. */
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/**
 * True when `value` is a real calendar date in `dd/mm/yyyy`.
 *
 * Still needed with a picker on screen: the value is a plain string in `FormState`, and nothing
 * stops a merchant's prefilled config, a restored session or a future typed input putting `31/02`
 * there. It is also what `validate.ts` and the legacy `collect()` both call.
 */
export function isValidDate(value) {
    return parseDate(value) !== null;
}
/** `dd/mm/yyyy` → its parts, or null when the string is not a real date. */
export function parseDate(value) {
    const m = dateRe.exec(value);
    if (!m)
        return null;
    const [day, month, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (month < 1 || month > 12 || day < 1)
        return null;
    if (day > daysInMonth(year, month))
        return null;
    return { year, month, day };
}
const pad = (n, width = 2) => String(n).padStart(width, '0');
/** Parts → `dd/mm/yyyy`, the one format this app stores and submits. */
export function formatDate(date) {
    return `${pad(date.day)}/${pad(date.month)}/${pad(date.year, 4)}`;
}
/**
 * Month names, **Spanish and unaccented**, matching every other hard-coded storefront string in
 * this package (`THANK_YOU`, the Designer's own defaults). `MessagesConfig` covers only the two
 * validation strings, so making these merchant-editable is a schema change with an admin surface
 * behind it — worth doing if a non-Spanish store ever ships, and out of scope until then.
 *
 * Not `Intl.DateTimeFormat`: the builder is pure and takes no locale, the worker's ICU data is not
 * ours to depend on, and a name that differs between the two bundles is exactly what dual-ship
 * cannot have.
 */
export const MONTH_NAMES = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
];
/**
 * The weekday the grid starts on, as `Date`'s 0-based day-of-week. **Sunday.**
 *
 * CLDR puts Colombia, Mexico, Brazil, Peru, Venezuela and most of Central America on a Sunday-first
 * week; Argentina, Chile and Uruguay are Monday-first. The stores this ships to are Colombian and
 * Mexican, so Sunday is the majority answer. One constant rather than a config knob: the setting
 * would have to exist on both bundles and be right on both, for a difference nobody has asked for.
 */
export const WEEK_START = 0;
/** Weekday captions, in `WEEK_START` order. Two letters so seven fit a narrow modal. */
export const WEEKDAY_NAMES = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
/** The label above the grid, e.g. `Marzo`. Out-of-range months return `''` rather than throwing. */
export function monthName(month) {
    return MONTH_NAMES[month - 1] ?? '';
}
/** Today, in the BUYER's local timezone — a delivery date is local, never UTC. */
export function todayYmd(now = new Date()) {
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}
/** The visible month, as `yyyy-mm`. A string so it lives in `FormState` beside the field values. */
export function formatCursor(year, month) {
    return `${pad(year, 4)}-${pad(month)}`;
}
/** `yyyy-mm` → its parts. Falls back to the current month for anything malformed. */
export function parseCursor(cursor, now) {
    const m = /^(\d{4})-(\d{2})$/.exec(cursor ?? '');
    const month = m ? Number(m[2]) : 0;
    if (!m || month < 1 || month > 12) {
        const today = todayYmd(now);
        return { year: today.year, month: today.month };
    }
    return { year: Number(m[1]), month };
}
/**
 * The month the picker should open on: the month of the value the buyer already chose, else the
 * current one. Opening on "today" when a date is already set would hide the buyer's own choice
 * behind a scroll — they expect the calendar to show them where they already are.
 */
export function openCursor(value, now) {
    const parsed = value ? parseDate(value) : null;
    if (parsed)
        return formatCursor(parsed.year, parsed.month);
    const today = todayYmd(now);
    return formatCursor(today.year, today.month);
}
/** Move the cursor by whole months, rolling the year over. */
export function shiftCursor(cursor, months, now) {
    const { year, month } = parseCursor(cursor, now);
    // `month - 1 + months` in 0-based space, then floor-divide so negatives roll backwards.
    const total = year * 12 + (month - 1) + months;
    return formatCursor(Math.floor(total / 12), (((total % 12) + 12) % 12) + 1);
}
/**
 * The six-week grid for a month: `null` for the leading and trailing blanks, a day number otherwise.
 *
 * **Always 6 rows (42 cells)**, even when 5 would hold the month. A grid that changes height moves
 * every block under it — inside a modal that scrolls, the buyer's thumb lands on a different day
 * than the one they were aiming at. Fixed height costs one empty row and removes the whole class.
 */
export function calendarWeeks(cursor, now) {
    const { year, month } = parseCursor(cursor, now);
    // UTC, so a timezone west of Greenwich cannot shift the 1st onto the previous day.
    const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    const lead = (firstWeekday - WEEK_START + 7) % 7;
    const total = daysInMonth(year, month);
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const day = i - lead + 1;
        cells.push(day >= 1 && day <= total ? day : null);
    }
    const weeks = [];
    for (let i = 0; i < 42; i += 7)
        weeks.push(cells.slice(i, i + 7));
    return weeks;
}
/** How far back and forward the year dropdown reaches. Wide enough for a birthday either way. */
const YEARS_BACK = 100;
const YEARS_FORWARD = 10;
/**
 * The years the dropdown offers, ascending.
 *
 * Always includes the year currently on screen, even when a stored value sits outside the window —
 * otherwise the select would silently show a different year than the grid.
 */
export function yearOptions(current, now) {
    const today = todayYmd(now);
    const from = Math.min(today.year - YEARS_BACK, current);
    const to = Math.max(today.year + YEARS_FORWARD, current);
    const years = [];
    for (let y = from; y <= to; y++)
        years.push(y);
    return years;
}
/**
 * Storefront copy for the picker, Spanish like every other hard-coded string here.
 *
 * `Año` keeps its tilde on purpose — it is the one word in this file where dropping the accent
 * changes the meaning to something a merchant would not want in their form's markup.
 */
export const CALENDAR_TEXT = {
    clear: 'Limpiar',
    month: 'Mes',
    year: 'Año',
    // Never rendered — these are the accessible names for the two arrows, which are glyph-only.
    // `button` takes an `ariaLabel` in NubeSDK (`nube-sdk-types@0.90.0`), so both bundles can carry
    // them; the legacy writes the same strings into `aria-label`.
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
};
//# sourceMappingURL=calendar.js.map